import type { Angle } from "./Angle";
import type { BasicSize } from "./BasicSize";
import {
	CoordinateSpace,
	type ICoordinateSpace,
	type InFullSizeImage,
	type InMonitorSetup,
	type InOverviewImage,
	type InTransformed,
	type InVisibleCanvas,
	type InVisibleCanvasDrawRegion,
	type InWindow,
} from "./CoordinateSpace";
import type { IPoint } from "./Point";
import type { ITransformation } from "./Transformation";
import type { IZoomStore } from "../viewports/viewport-series/zoom/ZoomStore";

export interface ISize<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly type: "size";
	readonly coordinateSpace: TCoordinateSpace;
	readonly width: number;
	readonly height: number;
	readonly aspectRatio: number;

	equals(other: ISize<TCoordinateSpace>): boolean;

	transform(transformation: ITransformation<TCoordinateSpace>): ISize<InTransformed<TCoordinateSpace>>;

	center(): IPoint<TCoordinateSpace>;

	/**
	 * Rotates this size as if it were a rectangle. This method will round to perfect multiples of 90°, so the end result will always be another perpendicular rectangle
	 * @param rotation
	 */
	rotate(rotation: Angle): ISize<TCoordinateSpace>;

	toBasicSize(): BasicSize;

	toString(): string;
}

export abstract class Size<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements ISize<TCoordinateSpace> {
	private _aspectRatio: number | null = null;

	public readonly width: number;
	public readonly height: number;
	public readonly type: "size";
	public readonly coordinateSpace: TCoordinateSpace;

	protected constructor(coordinateSpace: TCoordinateSpace, width: number, height: number) {
		this.coordinateSpace = coordinateSpace;
		this.width = width;
		this.height = height;
		this.type = "size";

		if (Number.isNaN(width)) {
			throw new Error("width is NaN");
		}

		if (Number.isNaN(height)) {
			throw new Error("height is NaN");
		}
	}

	/**
	 * Returns the width/height ratio or 0 if the height is 0
	 */
	get aspectRatio(): number {
		if (this._aspectRatio !== null) return this._aspectRatio;
		this._aspectRatio = this.height === 0 ? 0 : Math.round((1000 * this.width) / this.height) / 1000;
		return this._aspectRatio;
	}

	transform(transformation: ITransformation<TCoordinateSpace>): ISize<InTransformed<TCoordinateSpace>> {
		const { width, height } = this;
		const { degrees } = transformation.rotation.normalize();

		return new SizeInTransformed(this.coordinateSpace, transformation, degrees === 90 || degrees === 270 ? height : width, degrees === 90 || degrees === 270 ? width : height);
	}

	center(): IPoint<TCoordinateSpace> {
		const { width, height } = this;
		return this.coordinateSpace.createPoint(width / 2, height / 2);
	}

	rotate(rotation: Angle): ISize<TCoordinateSpace> {
		const { width, height } = this;
		const { degrees } = rotation;
		let rotatedWidth, rotatedHeight;
		if ((degrees > 85 && degrees < 95) || (degrees > 265 && degrees < 275)) {
			// noinspection JSSuspiciousNameCombination
			rotatedWidth = height;
			// noinspection JSSuspiciousNameCombination
			rotatedHeight = width;
		} else {
			rotatedWidth = width;
			rotatedHeight = height;
		}

		return this.coordinateSpace.createSize(rotatedWidth, rotatedHeight);
	}

	toBasicSize(): BasicSize {
		return {
			width: this.width,
			height: this.height,
		};
	}

	equals(other: ISize<TCoordinateSpace>) {
		return this.width === other.width && this.height === other.height;
	}

	toString() {
		const { width, height } = this;
		return `${width}x${height}px`;
	}

	static fromBasicSize<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(basicSize: BasicSize, coordinateSpace: TCoordinateSpace) {
		return coordinateSpace.createSize(basicSize.width, basicSize.height);
	}
}

export interface ISizeInWindow extends ISize<InWindow> {}

export interface ISizeInMonitorSetup extends ISize<InMonitorSetup> {}

export interface ISizeInVisibleCanvas extends ISize<InVisibleCanvas> {}

export interface ISizeInVisibleCanvasDrawRegion extends ISize<InVisibleCanvasDrawRegion> {}

export interface ISizeInOverviewImage extends ISize<InOverviewImage> {}

export interface ISizeInFullSizeImage extends ISize<InFullSizeImage> {}

export interface ISizeInTransformedInFullSizeImage extends ISize<InTransformed<InFullSizeImage>> {}

export class SizeInWindow extends Size<InWindow> implements ISizeInWindow {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inWindow, width, height);
	}
}

export const sizeInWindowToSizeInVisibleCanvas = (size: ISize<InWindow>) => {
	return new SizeInVisibleCanvas(size.width, size.height);
};

export const sizeInWindowToSizeInFullSizeImage = (zoomStore: IZoomStore) => (size: ISize<InWindow>) => {
	const { currentZoomFactor } = zoomStore;
	const { width, height } = size;
	return new SizeInFullSizeImage(width / currentZoomFactor!.value, height / currentZoomFactor!.value);
};

export class SizeInFullSizeImage extends Size<InFullSizeImage> implements ISizeInFullSizeImage {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inFullSizeImage, width, height);
	}
}

export class SizeInOverviewImage extends Size<InOverviewImage> implements ISizeInOverviewImage {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inOverviewImage, width, height);
	}
}

export class SizeInVisibleCanvas extends Size<InVisibleCanvas> implements ISizeInVisibleCanvas {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inVisibleCanvas, width, height);
	}
}

export class SizeInVisibleCanvasDrawRegion extends Size<InVisibleCanvasDrawRegion> implements ISizeInVisibleCanvasDrawRegion {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inVisibleCanvasDrawRegion, width, height);
	}
}

export class SizeInMonitorSetup extends Size<InMonitorSetup> implements ISizeInMonitorSetup {
	constructor(width: number, height: number) {
		super(CoordinateSpace.inMonitorSetup, width, height);
	}
}

export class SizeInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Size<InTransformed<TCoordinateSpace>> {
	constructor(originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>, transformation: ITransformation<TCoordinateSpace>, width: number, height: number) {
		super(CoordinateSpace.inTransformed(originalCoordinateSpace, transformation), width, height);
	}
}

export class SizeInTransformedInFullSizeImage extends SizeInTransformed<InFullSizeImage> implements ISizeInTransformedInFullSizeImage {
	constructor(transformation: ITransformation<InFullSizeImage>, width: number, height: number) {
		super(CoordinateSpace.inFullSizeImage, transformation, width, height);
	}
}

export const sizeInFullSizeImageToSizeInTransformedFullSizeImage =
	() =>
	(transformation: ITransformation<InFullSizeImage>, size: ISizeInFullSizeImage): ISizeInTransformedInFullSizeImage => {
		return size.transform(transformation);
	};

export const transformSizeToOriginal =
	<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: InTransformed<TCoordinateSpace>) =>
	(size: ISize<InTransformed<TCoordinateSpace>>) => {
		const { width, height } = size;
		const { originalCoordinateSpace, transformation } = coordinateSpace;
		const { degrees } = transformation.rotation;

		const originalWidth = degrees === 90 || degrees === 270 ? height : width;
		const originalHeight = degrees === 90 || degrees === 270 ? width : height;

		return originalCoordinateSpace.createSize(originalWidth, originalHeight);
	};
